// ABOUTME: Mixpanel analytics integration for tracking anonymous user properties and events
// ABOUTME: Lazy-loads mixpanel-browser to keep it out of the main bundle

import config from "../config"
import { foreignServicesAllowed } from "./domain"
import type { OverridedMixpanel } from "mixpanel-browser"

const MIXPANEL_TOKEN = config.MIXPANEL_TOKEN || undefined

let mixpanelPromise: Promise<OverridedMixpanel> | null = null

// Lazy-load mixpanel-browser and initialize it on first use
const getMixpanel = async (): Promise<OverridedMixpanel | null> => {
  if (!MIXPANEL_TOKEN || !foreignServicesAllowed()) return null

  if (!mixpanelPromise) {
    mixpanelPromise = import("mixpanel-browser").then((mod) => {
      const mixpanel = mod.default
      // No `record_*` options: session recording is off. It filmed every user's
      // screen — masking only what carries `.sensitive-data` — which is far more
      // personal data than any profile field we send, and it pulled a separate
      // ~340 KB recorder from Mixpanel's CDN on top of that.
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: config.IS_DEV,
        track_pageview: true,
        persistence: "localStorage",
        api_host: "https://api-eu.mixpanel.com",
      })
      mixpanel.register({
        app_version: config.BUILD_VERSION,
        environment: config.MODE,
      })
      return mixpanel
    })
  }

  return mixpanelPromise
}

export const initMixpanel = (): void => {
  if (!MIXPANEL_TOKEN || !foreignServicesAllowed()) return
  // Defer initialization to after first paint to keep main bundle light
  const schedule = (cb: () => void) => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(cb, { timeout: 3000 })
    } else {
      setTimeout(cb, 2000)
    }
  }
  schedule(() => {
    void getMixpanel()
  })
}

/**
 * The user is a pseudonym here: their id and the shape of their account, nothing
 * that names them. Name and email used to travel along; they told us nothing the
 * id doesn't, and our users include people in Russia, for whom this is a transfer
 * of personal data abroad.
 */
export const setMixpanelUserProperties = async (userId: string, isPro: boolean, isCoach: boolean): Promise<void> => {
  const mixpanel = await getMixpanel()
  if (!mixpanel) return

  mixpanel.identify(userId)

  mixpanel.people.set_once({
    $user_id: userId,
    $created: new Date().toISOString(),
    distinct_id: userId,
  })

  mixpanel.people.set({
    is_pro: isPro,
    is_coach: isCoach,
    account_type: isCoach ? "coach" : "athlete",
    subscription_status: isPro ? "pro" : "free",
    last_seen: new Date().toISOString(),
  })

  mixpanel.register({
    user_id: userId,
    is_pro: isPro,
    is_coach: isCoach,
    account_type: isCoach ? "coach" : "athlete",
    app_version: config.BUILD_VERSION,
  })

  mixpanel.track("User Identified", {
    user_id: userId,
    is_pro: isPro,
    is_coach: isCoach,
    app_version: config.BUILD_VERSION,
  })
}

export const trackMixpanelEvent = async (eventName: string, properties?: Record<string, unknown>): Promise<void> => {
  const mixpanel = await getMixpanel()
  if (!mixpanel) return

  mixpanel.track(eventName, {
    ...properties,
    app_version: config.BUILD_VERSION,
  })
}
